import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, ChevronDown, ChevronRight, ClipboardList } from 'lucide-react';
import { Input, Modal, Select, message } from 'antd';
import { useDefaultYear } from '@/features/settings/hooks/useDashboardSettingsQuery';
import { useBudgetReportYearsListQuery } from '@/features/budgets/hooks/useBudgetQueries';
import { useGovernoratesQuery } from '@/features/region/hooks/useRegionQueries';
import { schoolWithFiltersApi } from '@/features/reports/api';
import { complianceApi } from '@/features/reports/api/compliance.api';
import {
  ComplianceActivityType,
  ComplianceFollowUpSource,
  ComplianceFollowUpStatus,
  type ComplianceFollowUpDto,
} from '@/features/reports/api/compliance.dto';
import { Button } from '@/shared/components/Button';
import {
  FollowUpActivityList,
  type FollowUpStatus,
  type FollowUpTask,
} from '@/features/reports/components/FollowUpsEditor';
import { ApiError } from '@/shared/api';

type ActionMode = 'takeAction' | 'addReview';

const EXPANDED_ROW_COL_SPAN = 8;

function statusNumberToLabel(n: number): FollowUpStatus {
  if (n === ComplianceFollowUpStatus.InReview) return 'In review';
  if (n === ComplianceFollowUpStatus.Resolved) return 'Resolved';
  return 'Open';
}

function statusLabelToNumber(s: FollowUpStatus): number {
  if (s === 'In review') return ComplianceFollowUpStatus.InReview;
  if (s === 'Resolved') return ComplianceFollowUpStatus.Resolved;
  return ComplianceFollowUpStatus.Open;
}

function sourceLabel(n: number): string {
  if (n === ComplianceFollowUpSource.RiskTriage) return 'Risk Triage';
  return 'School Profile';
}

function statusBadgeClass(n: number): string {
  if (n === ComplianceFollowUpStatus.Resolved) return 'bg-emerald-100 text-emerald-800';
  if (n === ComplianceFollowUpStatus.InReview) return 'bg-amber-100 text-amber-800';
  return 'bg-slate-100 text-slate-800';
}

function sourceBadgeClass(n: number): string {
  if (n === ComplianceFollowUpSource.RiskTriage) return 'bg-rose-100 text-rose-800';
  return 'bg-sky-100 text-sky-800';
}

function formatDate(iso: string): string {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return iso;
  }
}

function mapDtoToTask(dto: ComplianceFollowUpDto): FollowUpTask {
  return {
    id: dto.id,
    description: dto.description,
    deadline: dto.deadline,
    reviews: (dto.activities ?? []).map((a) => ({
      id: a.id,
      note: a.note,
      status:
        a.type === ComplianceActivityType.TakeAction && a.newStatus != null
          ? statusNumberToLabel(a.newStatus)
          : undefined,
      createdAt: a.creationTime,
      createdByName: a.userName || dto.creatorName || 'Unknown',
    })),
    createdAt: dto.creationTime,
    createdByName: dto.creatorName || dto.activities?.[0]?.userName || 'Unknown',
    currentStatus: statusNumberToLabel(dto.currentStatus),
  };
}

/** Prefer API `schoolName`; fallback to school filter labels when list omits name. */
function resolveSchoolDisplayName(
  item: ComplianceFollowUpDto,
  schoolLabelById: Map<string, string>
): string {
  const fromApi = item.schoolName?.trim();
  if (fromApi) return fromApi;
  const fromFilter = schoolLabelById.get(item.schoolId)?.trim();
  if (fromFilter) return fromFilter;
  const sid = item.schoolId?.trim();
  if (!sid) return '—';
  return sid.length > 8 ? `${sid.slice(0, 8)}…` : sid;
}

export function CompliancesPage() {
  const defaultYear = useDefaultYear();
  const reportYearsQuery = useBudgetReportYearsListQuery();
  const governoratesQuery = useGovernoratesQuery();

  const [year, setYear] = useState<string>('');
  const [governorateId, setGovernorateId] = useState<number | undefined>(undefined);
  const [schoolIds, setSchoolIds] = useState<string[]>([]);
  const [schoolOptions, setSchoolOptions] = useState<{ value: string; label: string }[]>([]);
  const [schoolsLoading, setSchoolsLoading] = useState(false);

  const [data, setData] = useState<ComplianceFollowUpDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const [actionTask, setActionTask] = useState<FollowUpTask | null>(null);
  const [actionMode, setActionMode] = useState<ActionMode>('takeAction');
  const [reviewNote, setReviewNote] = useState('');
  const [reviewStatus, setReviewStatus] = useState<FollowUpStatus>('Open');
  const [actionSaving, setActionSaving] = useState(false);

  useEffect(() => {
    if (!year && defaultYear) setYear(defaultYear);
  }, [defaultYear, year]);

  useEffect(() => {
    if (governorateId == null) {
      setSchoolOptions([]);
      return;
    }
    let cancelled = false;
    setSchoolsLoading(true);
    schoolWithFiltersApi
      .getList({ governorateId, maxResultCount: 500 })
      .then((res) => {
        if (cancelled) return;
        setSchoolOptions(
          res.items
            .filter((s) => s.schoolName)
            .map((s) => ({ value: s.id, label: s.schoolName ?? s.id }))
        );
      })
      .catch(() => {
        if (!cancelled) setSchoolOptions([]);
      })
      .finally(() => {
        if (!cancelled) setSchoolsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [governorateId]);

  const schoolLabelById = useMemo(() => {
    const m = new Map<string, string>();
    for (const o of schoolOptions) {
      if (o.value && o.label) m.set(o.value, o.label);
    }
    return m;
  }, [schoolOptions]);

  const yearOptions = useMemo(() => {
    const raw = reportYearsQuery.data ?? [];
    return [...raw].sort((a, b) => b - a).map((n) => ({ value: String(n), label: String(n) }));
  }, [reportYearsQuery.data]);

  const governorateOptions = useMemo(() => {
    return (governoratesQuery.data ?? []).map((g) => ({ value: g.id, label: g.name }));
  }, [governoratesQuery.data]);

  const fetchCompliances = useCallback(async () => {
    const yearNum = parseInt(year, 10);
    if (!Number.isFinite(yearNum) || yearNum <= 0) return;

    setLoading(true);
    try {
      if (schoolIds.length > 0) {
        const allResults = await Promise.all(
          schoolIds.map((sid) => complianceApi.getList({ schoolId: sid, year: yearNum, maxResultCount: 200 }))
        );
        const merged = allResults.flatMap((r) => r.items);
        merged.sort((a, b) => new Date(b.creationTime).getTime() - new Date(a.creationTime).getTime());
        setData(merged);
      } else {
        const res = await complianceApi.getList({ year: yearNum, maxResultCount: 200 });
        setData(res.items);
      }
    } catch (e) {
      console.error('[CompliancesPage] fetch failed:', e);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [year, schoolIds]);

  const toggleExpand = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const openAction = useCallback((task: FollowUpTask, mode: ActionMode, e: React.MouseEvent) => {
    e.stopPropagation();
    setActionTask(task);
    setActionMode(mode);
    setReviewNote('');
    setReviewStatus(task.currentStatus);
  }, []);

  const submitReview = useCallback(async () => {
    if (!actionTask) return;
    const note = reviewNote.trim();
    if (!note) {
      message.error('Review note is required.');
      return;
    }
    const keepExpandedId = actionTask.id;
    setActionSaving(true);
    try {
      if (actionMode === 'addReview') {
        await complianceApi.addReview(actionTask.id, { note });
        message.success('Review saved.');
      } else {
        await complianceApi.takeAction(actionTask.id, {
          newStatus: statusLabelToNumber(reviewStatus),
          note,
        });
        message.success('Status updated and review saved.');
      }
      setActionTask(null);
      await fetchCompliances();
      setExpandedIds((prev) => new Set(prev).add(keepExpandedId));
    } catch (e) {
      const msg =
        e instanceof ApiError ? ApiError.getUserMessage(e) : e instanceof Error ? e.message : 'Could not save.';
      message.error(msg);
    } finally {
      setActionSaving(false);
    }
  }, [actionTask, actionMode, reviewNote, reviewStatus, fetchCompliances]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">Compliances</h1>
          <p className="mt-1 text-sm text-slate-600">
            View all compliance follow-ups across School Profile and Risk Triage.
          </p>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200/90 bg-white p-5 shadow-sm ring-1 ring-slate-100">
        <div className="flex flex-wrap items-end gap-4">
          <div className="min-w-[10rem]">
            <label className="mb-1 block text-xs font-semibold text-slate-700">Year</label>
            <Select
              className="w-full"
              placeholder="Select year"
              value={year || undefined}
              onChange={(v) => setYear(v)}
              options={yearOptions}
              loading={reportYearsQuery.isPending}
              allowClear
            />
          </div>
          <div className="min-w-[12rem]">
            <label className="mb-1 block text-xs font-semibold text-slate-700">Governorate</label>
            <Select
              className="w-full"
              placeholder="All governorates"
              value={governorateId}
              onChange={(v) => {
                setGovernorateId(v);
                setSchoolIds([]);
              }}
              options={governorateOptions}
              loading={governoratesQuery.isPending}
              allowClear
              showSearch
              optionFilterProp="label"
            />
          </div>
          <div className="min-w-[16rem] flex-1">
            <label className="mb-1 block text-xs font-semibold text-slate-700">Schools</label>
            <Select
              className="w-full"
              mode="multiple"
              placeholder={governorateId ? 'Select schools…' : 'Select a governorate first'}
              value={schoolIds}
              onChange={(v) => setSchoolIds(v)}
              options={schoolOptions}
              loading={schoolsLoading}
              disabled={!governorateId}
              allowClear
              showSearch
              optionFilterProp="label"
              maxTagCount={3}
            />
          </div>
          <div>
            <Button
              type="button"
              variant="primary"
              className="px-5 py-2 text-sm"
              onClick={fetchCompliances}
              disabled={!year}
            >
              Search
            </Button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="size-7 animate-spin text-indigo-600" />
        </div>
      ) : data.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/80 px-6 py-12 text-center">
          <p className="text-sm text-slate-600">
            No compliance follow-ups found. Select a year and click <strong>Search</strong>.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-xs font-medium text-slate-500">
            Showing {data.length} compliance follow-up{data.length !== 1 ? 's' : ''}
          </p>
          <div className="overflow-hidden rounded-xl border border-slate-200/90 bg-white shadow-sm ring-1 ring-slate-100">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50/60">
                  <th className="w-8 px-3 py-3" aria-label="Expand" />
                  <th className="min-w-[10rem] px-3 py-3 font-semibold text-slate-700">School</th>
                  <th className="px-3 py-3 font-semibold text-slate-700">Source</th>
                  <th className="px-3 py-3 font-semibold text-slate-700">Description</th>
                  <th className="px-3 py-3 font-semibold text-slate-700">Deadline</th>
                  <th className="px-3 py-3 font-semibold text-slate-700">Status</th>
                  <th className="px-3 py-3 font-semibold text-slate-700">Created</th>
                  <th className="px-3 py-3 text-center font-semibold text-slate-700">Activities</th>
                  <th className="min-w-[11rem] px-3 py-3 text-right font-semibold text-slate-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.map((item) => {
                  const expanded = expandedIds.has(item.id);
                  const task = mapDtoToTask(item);
                  const schoolDisplayName = resolveSchoolDisplayName(item, schoolLabelById);
                  return (
                    <Fragment key={item.id}>
                      <tr
                        className="cursor-pointer border-b border-slate-100 transition-colors hover:bg-slate-50/60"
                        onClick={() => toggleExpand(item.id)}
                      >
                        <td className="px-3 py-3">
                          {expanded ? (
                            <ChevronDown className="size-4 text-slate-400" />
                          ) : (
                            <ChevronRight className="size-4 text-slate-400" />
                          )}
                        </td>
                        <td className="max-w-[14rem] px-3 py-3 font-medium text-slate-900">
                          {schoolDisplayName}
                        </td>
                        <td className="px-3 py-3">
                          <span
                            className={`inline-block rounded px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide ${sourceBadgeClass(item.source)}`}
                          >
                            {sourceLabel(item.source)}
                          </span>
                        </td>
                        <td className="max-w-[16rem] truncate px-3 py-3 text-slate-700" title={item.description}>
                          {item.description}
                        </td>
                        <td className="px-3 py-3 text-slate-600">{formatDate(item.deadline)}</td>
                        <td className="px-3 py-3">
                          <span
                            className={`inline-block rounded px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide ${statusBadgeClass(item.currentStatus)}`}
                          >
                            {statusNumberToLabel(item.currentStatus)}
                          </span>
                        </td>
                        <td className="px-3 py-3 text-slate-600">{formatDate(item.creationTime)}</td>
                        <td className="px-3 py-3 text-center text-slate-600">{item.activities?.length ?? 0}</td>
                        <td className="px-3 py-3 text-right">
                          <div className="flex flex-wrap justify-end gap-1.5">
                            <Button
                              type="button"
                              variant="secondary"
                              className="px-2 py-1 text-[11px]"
                              onClick={(e) => openAction(task, 'takeAction', e)}
                            >
                              Take action
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              className="px-2 py-1 text-[11px]"
                              onClick={(e) => openAction(task, 'addReview', e)}
                            >
                              Add review
                            </Button>
                          </div>
                        </td>
                      </tr>
                      {expanded && (
                        <tr className="border-b border-slate-100 bg-slate-50/40">
                          <td />
                          <td colSpan={EXPANDED_ROW_COL_SPAN} className="px-4 py-4">
                            <FollowUpActivityList task={task} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Modal
        title={
          <span className="inline-flex items-center gap-2">
            <ClipboardList className="size-4 text-slate-600" aria-hidden />
            {actionMode === 'addReview' ? 'Add review (note only)' : 'Take action — status update'}
          </span>
        }
        open={Boolean(actionTask)}
        onCancel={() => setActionTask(null)}
        onOk={() => void submitReview()}
        okText={actionMode === 'addReview' ? 'Save review' : 'Save status & review'}
        confirmLoading={actionSaving}
        destroyOnClose
        maskClosable
      >
        {actionTask ? (
          <div className="flex flex-col gap-3 pt-1">
            <div className="rounded-md border border-slate-100 bg-slate-50 px-3 py-2 text-xs text-slate-700">
              <p className="font-semibold text-slate-900">{actionTask.description}</p>
              <p className="mt-1 text-slate-600">Deadline: {formatDate(actionTask.deadline)}</p>
              <p className="mt-1 text-slate-600">
                Current status:{' '}
                <span className="font-semibold text-slate-900">{actionTask.currentStatus}</span>
              </p>
            </div>
            {actionMode === 'addReview' ? (
              <p className="rounded-md border border-sky-100 bg-sky-50/80 px-3 py-2 text-xs text-sky-950">
                This adds a note to the activity log only. It does <strong>not</strong> change the follow-up status.
                Use <strong>Take action</strong> to update status.
              </p>
            ) : null}
            {actionTask.reviews.length > 0 ? (
              <div className="max-h-56 overflow-y-auto rounded-md border border-slate-200 bg-white px-3 py-2">
                <p className="mb-2 text-[10px] font-bold uppercase tracking-wide text-slate-500">Activity so far</p>
                <FollowUpActivityList task={actionTask} />
              </div>
            ) : null}
            <div>
              <label htmlFor="compliance-review-note" className="mb-1 block text-xs font-semibold text-slate-700">
                Review note <span className="text-red-600">*</span>
              </label>
              <Input.TextArea
                id="compliance-review-note"
                rows={4}
                value={reviewNote}
                onChange={(e) => setReviewNote(e.target.value)}
                placeholder="Document review outcome or next steps…"
              />
            </div>
            {actionMode === 'takeAction' ? (
              <div>
                <label htmlFor="compliance-review-status" className="mb-1 block text-xs font-semibold text-slate-700">
                  New status <span className="text-red-600">*</span>
                </label>
                <Select
                  id="compliance-review-status"
                  className="w-full"
                  value={reviewStatus}
                  onChange={(v) => setReviewStatus(v as FollowUpStatus)}
                  options={(
                    [
                      { value: 'Open', label: 'Open' },
                      { value: 'In review', label: 'In review' },
                      { value: 'Resolved', label: 'Resolved' },
                    ] as const
                  ).map((o) => ({ value: o.value, label: o.label }))}
                />
              </div>
            ) : null}
          </div>
        ) : null}
      </Modal>
    </div>
  );
}
